// One form field for an indicator or strategy input (TradingView's Inputs tab),
// and the rules for keeping its value valid.

export interface FormInput {
  readonly id: string
  readonly type: string
  readonly title?: string | undefined
  readonly defval?: unknown
  readonly min?: number | undefined
  readonly max?: number | undefined
  readonly step?: number | undefined
  readonly options?: ReadonlyArray<string> | undefined
  /** What the select shows for an option, when not the option itself. */
  readonly optionLabels?: Readonly<Record<string, string>> | undefined
  readonly group?: string | undefined
  readonly tooltip?: string | undefined
  readonly active?: unknown
}

const SOURCES = ["open", "high", "low", "close", "hl2", "hlc3", "ohlc4", "hlcc4"]

/** Whether an input is enabled, given the others (e.g. "MA length" only when an MA type is chosen). */
export const isActive = (input: FormInput, values: Record<string, unknown>): boolean => {
  const active = input.active
  if (active === undefined || typeof active === "boolean") return (active as boolean | undefined) ?? true
  const condition = active as { input: string; eq?: unknown; ne?: unknown }
  const value = values[condition.input]
  if ("eq" in condition) return value === condition.eq
  if ("ne" in condition) return value !== condition.ne
  return true
}

/**
 * Keeps a number inside the input's range (int inputs whole). Done here rather
 * than by the browser: its step check counts from `min` (BB StdDev: min 0.001,
 * step 0.5) and would refuse ordinary values such as 2.
 */
export const normalise = (input: FormInput, value: unknown): unknown => {
  if (input.type !== "int" && input.type !== "float") return value
  let n = Number(value)
  if (!Number.isFinite(n)) return input.defval
  if (input.type === "int") n = Math.round(n)
  if (input.min !== undefined) n = Math.max(input.min, n)
  if (input.max !== undefined) n = Math.min(input.max, n)
  return n
}

export function InputField({ input, onChange, value }: {
  readonly input: FormInput
  readonly value: unknown
  readonly onChange: (value: unknown) => void
}) {
  switch (input.type) {
    case "int":
    case "float":
      return (
        <input
          type="number"
          value={String(value ?? "")}
          min={input.min}
          max={input.max}
          step={input.type === "int" ? 1 : "any"}
          onChange={(event) => onChange(event.target.value === "" ? "" : Number(event.target.value))}
        />
      )
    case "bool":
      return <input type="checkbox" checked={value === true} onChange={(event) => onChange(event.target.checked)} />
    case "date":
      return <input type="date" value={String(value ?? "")} onChange={(event) => onChange(event.target.value)} />
    case "source":
      return (
        <select value={String(value)} onChange={(event) => onChange(event.target.value)}>
          {SOURCES.map((source) => <option key={source} value={source}>{source}</option>)}
        </select>
      )
    default:
      return input.options !== undefined
        ? (
          <select value={String(value)} onChange={(event) => onChange(event.target.value)}>
            {input.options.map((option) => <option key={option} value={option}>{input.optionLabels?.[option] ?? option}</option>)}
          </select>
        )
        : <input type="text" value={String(value ?? "")} onChange={(event) => onChange(event.target.value)} />
  }
}

/** Inputs grouped as their `group` says, as fieldsets. */
export function InputGroups({ inputs, onChange, values }: {
  readonly inputs: ReadonlyArray<FormInput>
  readonly values: Record<string, unknown>
  readonly onChange: (id: string, value: unknown) => void
}) {
  const groups = [...new Set(inputs.map((input) => input.group ?? ""))]
  return groups.map((group) => (
    <fieldset key={group}>
      {group && <legend>{group}</legend>}
      {inputs.filter((input) => (input.group ?? "") === group).map((input) => (
        <label key={input.id} title={input.tooltip} className={isActive(input, values) ? undefined : "disabled"}>
          <span>{input.title ?? input.id}</span>
          <InputField input={input} value={values[input.id]} onChange={(value) => onChange(input.id, value)} />
        </label>
      ))}
    </fieldset>
  ))
}

/** The values that differ from the defaults, normalised. */
export const changedValues = (
  inputs: ReadonlyArray<FormInput>,
  values: Record<string, unknown>,
  defaults: Record<string, unknown>
): Record<string, unknown> =>
  Object.fromEntries(
    inputs
      .map((input) => [input.id, normalise(input, values[input.id])] as const)
      .filter(([id, value]) => value !== defaults[id])
  )
